﻿import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateLegalInfoMutation } from "@/features/organizers/api";
import { showApiError } from "@/lib/api-client";
import type { OrganizerSessionResponse } from "@ticketnjoy/types";

/**
 * KVKK + mesafeli satış + faturalandırma için zorunlu firma bilgileri.
 * Tüm alanlar doldurulmadıkça organizer mağazasını Active'e alamaz
 * (backend guard: ShopHandler.ChangeStatusAsync).
 *
 * `readOnly`: kayıt endpoint'i OwnerOnly; Manager formu görür ama düzenleyemez.
 */
export function LegalInfoForm({
  organizer,
  readOnly = false,
}: {
  organizer: OrganizerSessionResponse;
  readOnly?: boolean;
}) {
  const t = useT("settings.legal");
  const tCommon = useT("common");
  const tSettings = useT("settings");
  const updateLegalInfo = useUpdateLegalInfoMutation();

  const schema = z.object({
    legalName: z
      .string()
      .trim()
      .min(2, t("errors.legalNameRequired"))
      .max(200),
    // TCKN 11 hane, VKN 10 hane; salt rakam. Başında sıfır olabilir → string tutuyoruz.
    taxNumber: z
      .string()
      .trim()
      .regex(/^\d{10,11}$/, t("errors.taxNumberFormat")),
    taxOffice: z
      .string()
      .trim()
      .min(2, t("errors.taxOfficeRequired"))
      .max(100),
    billingAddress: z
      .string()
      .trim()
      .min(10, t("errors.billingAddressRequired"))
      .max(500),
  });

  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      legalName: organizer.legalName ?? "",
      taxNumber: organizer.taxNumber ?? "",
      taxOffice: organizer.taxOffice ?? "",
      billingAddress: organizer.billingAddress ?? "",
    },
  });

  // Organizer değiştiğinde (mutation sonrası query refetch vs) form'u resetle
  // ki stale defaultValue'lar kullanıcı yazarken üzerine yazmasın.
  useEffect(() => {
    form.reset({
      legalName: organizer.legalName ?? "",
      taxNumber: organizer.taxNumber ?? "",
      taxOffice: organizer.taxOffice ?? "",
      billingAddress: organizer.billingAddress ?? "",
    });
    // form.reset stable; organizer field'ları değişince sadece yenile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizer.legalName,
    organizer.taxNumber,
    organizer.taxOffice,
    organizer.billingAddress,
  ]);

  async function onSubmit(values: Values) {
    try {
      await updateLegalInfo.mutateAsync(values);
      toast.success(t("saved"));
    } catch (err) {
      showApiError(err, tCommon("unexpectedError"));
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">{t("title")}</CardTitle>
        </div>
        <CardDescription className="text-xs">
          {t("description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tamamlanma durumu banner'ı. incomplete → ambar uyarısı (mağaza
            yayına alınamaz); complete → pozitif teyit. */}
        {organizer.isLegalInfoComplete ? (
          <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/50 dark:text-emerald-200">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t("completeNotice")}</span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/50 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t("incompleteWarning")}</span>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="legalName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("legalName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("legalNamePlaceholder")}
                      autoComplete="organization"
                      {...field}
                      disabled={readOnly}
                    />
                  </FormControl>
                  <FormDescription className="text-xs">
                    {t("legalNameHint")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="taxNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("taxNumber")}</FormLabel>
                    <FormControl>
                      <Input
                        inputMode="numeric"
                        placeholder={t("taxNumberPlaceholder")}
                        maxLength={11}
                        {...field}
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      {t("taxNumberHint")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taxOffice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("taxOffice")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("taxOfficePlaceholder")}
                        {...field}
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="billingAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("billingAddress")}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t("billingAddressPlaceholder")}
                      autoComplete="street-address"
                      {...field}
                      disabled={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {readOnly ? (
              <p className="pt-2 text-xs text-muted-foreground">
                {tSettings("ownerOnly")}
              </p>
            ) : (
              <div className="flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={updateLegalInfo.isPending || !form.formState.isDirty}
                >
                  {updateLegalInfo.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {tCommon("saving")}
                    </>
                  ) : (
                    tCommon("save")
                  )}
                </Button>
              </div>
            )}
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
